import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Gantt, Tooltip, Willow } from "@svar-ui/react-gantt";
import "@svar-ui/react-gantt/all.css";
import { useGanttView } from "../hooks/useGanttView.js";
import styles from './TaskGanttView.module.scss';
import { useTaskOptions } from "../hooks/useTaskOptions.js";
import ShowTaskLink from "../show/ShowTaskLink.jsx";
import ShowWorkPackageLink from "../show/ShowWorkPackageLink.jsx";
import TooltipWithRef from "../../common/Tooltip.jsx";
import { useGanttTodayLine } from "../hooks/useGanttTodayLine.js";
import Button from "../../bootstrap/Button.jsx";
import { FaIcon } from "../../common/Icons.jsx";

import {
  task_list_json_path
} from '~/routes';

const month = {
  unit: 'month',
  step: 1, 
  format: (date) => {
    const months = I18n.t('month_names');
    const monthName = months[date.getMonth()];
    return `${monthName} ${date.getFullYear()}`;
  }
};

const getWeekStart = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff);
  return d;
};

const getISOWeek = (date) => {
  const d = getWeekStart(date);
  d.setDate(d.getDate() + 3);
  const firstThursday = new Date(d.getFullYear(), 0, 4);
  const firstWeekStart = getWeekStart(firstThursday);
  return 1 + Math.round((d - firstWeekStart) / 604800000);
};

const week = {
  unit: 'week',
  step: 1,
  format: (date) => I18n.t('week_number', { number: getISOWeek(date) })
};

const day = { unit: 'day', step: 1, format: '%d' };

const SCALES = {
  day: [month, week, day],
  week: [month, week],
  month: [month]
}

const TaskTooltip = ({ data }) => {
  if (!data) return null;
  return (
    <div style={{ whiteSpace: 'nowrap' }}>
      <div>{data.text}</div>
      {data.description ? <div>{data.description}</div> : null}
      {data.start ? <div>{data.dates}</div> : null}
    </div>
  );
};

function TaskGanttView({
  programmeId,
  projectId,
  presetId = null,
  programmeTasks,
  scale,
  ganttView,
  permissions = {},
  advancedFeatures
}) {
  const [editMode, setEditMode] = useState(false);
  const api = useRef(null);
  const { task_status_options = [] } = useTaskOptions({ projectId });
  const [globalFilterVal, setGlobalFilterVal] = useState('');
  const fetchPath = task_list_json_path({ project_id: projectId })
  const fetchBody = { project_id: projectId, preset_id: presetId, programme_tasks: programmeTasks }
  const { tasks, links, init } = useGanttView({editMode, fetchPath, fetchBody, quickFilterText: globalFilterVal, filterModel: {}, projectId, permissions});
  const { highlightTime, todayOffset } = useGanttTodayLine({ scale });
  const canEdit = permissions.create_link || permissions.update_task;

  useEffect(() => {
    // Init the search bar as it render in ruby
    $('#datagridGlobalFilterInput').on("keyup", function(e) {
        setGlobalFilterVal(e.target.value)
    });
  }, []);

  const taskTypes = useMemo(() => {
    const unique = Array.from(new Set(task_status_options.map(s => s.status)));
    return [
      { id: 'phase', label: I18n.t('phase') },
      { id: 'work-package', label: I18n.t('work_package') },
      { id: 'milestone', label: I18n.t('milestone') },
      // { id: 'phase-unassigned', label: I18n.t('phase') },
      // { id: 'work-package-unassigned', label: I18n.t('work_package') },

      ...unique.flatMap(name => ([
        { id: `task-${name}`, label: name },
      ]))
    ];
  }, [task_status_options]);
  
  const scales = useMemo(() => {
    return SCALES?.[scale] || SCALES['week'];
  }, [scale]);

  const editor = useMemo(() => ({ sections: [] }), []);

  const columns = useMemo(() => {
    if (ganttView !== 'panel_on') return [];
    return [
      {
        id: 'text',
        header: I18n.t('task'),
        flexgrow: 2,
        cell: ({ row, column }) => {
          if (row.type === 'work-package' || row.type === 'work-package-zero') {
            const wpId = row.id.split(':')[1];
            if (wpId === 'unassigned') {
              return row.text;
            }
            return <ShowWorkPackageLink 
              triggerLabel={row.text}
              programmeId={programmeId}
              projectId={projectId}
              work_package_id={wpId}
              permissions={permissions}
            />
          }
          return <ShowTaskLink
            identifier={row.identifier}
            triggerLabel={row.text}
            label={row.text}
            programmeId={programmeId}
            projectId={projectId}
            permissions={permissions}
            advancedFeatures={advancedFeatures}
          />
        },
      },
      { id: 'status', header: I18n.t('status'), align: 'center', flexgrow: 1, sort: false,
        cell: ({ row }) => (
          <span className={`label task-status ${row.status}`}>
            {row.status_label}
          </span>
        )
      },
      { id: 'dates', header: I18n.t('date'), align: 'center', flexgrow: 1,
        cell: ({ row }) => (
          <TooltipWithRef title={row.dates}>
            {row.dates}
          </TooltipWithRef>
        )
      },
      { id: 'phase', header: I18n.t('phase'), align: 'center', flexgrow: 1, sort: false },
      { id: 'duration', header: I18n.t('duration'), align: 'center', width: 50, flexgrow: 1 },
      { id: 'estimated_work_days', header: I18n.t('estimated_work_days'), align: 'center', flexgrow: 1 }
    ]
  }, [ganttView]);

  return (
    <div className={`gantt-view ${editMode ? 'edit-mode' : ''} ${styles['gantt-view']}`} style={{ width: "100%", '--today-offset': todayOffset }}>
      {canEdit && <Button type={editMode ? 'default' : 'primary'} onClick={() => setEditMode(prev => !prev)}>
        {editMode ? I18n.t('cancel') : <><FaIcon type='pencil' /> {I18n.t('edit')}</>}
      </Button>}
      <Willow>
        <Tooltip api={api.current} content={TaskTooltip}>
          <Gantt
            init={init}
            ref={api}
            tasks={tasks}
            taskTypes={taskTypes}
            links={links}
            scales={scales}
            rowHeight={36}
            readonly={!editMode}
            editor={editor}
            columns={columns}
            highlightTime={highlightTime}
          />
        </Tooltip>
      </Willow>
    </div>
  );
}

export default TaskGanttView;